import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import {
  MotifLabel,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { Button } from "../CommonUIComponents/Button/Button";
import MultiPeoplePicker from "../CommonUIComponents/peoplePicker/MultiPeoplePicker";
import useADUsers from "../../hooks/useADUsers";
import { useI18n } from "../../hooks/useI18n";
import {
  fetchRegionsThunk,
  fetchCountriesThunk,
  fetchNearshoreCountriesThunk,
  clearRegions,
  clearCountries,
} from "../../store/slices/userManagementSlice";
import { isGdsEmail } from "../../util/roleHelpers";
import "./AddUserModal.scss";

const darkSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: "#23232f",
    border: state.isFocused ? "1px solid #ffe600" : "1px solid #747480",
    borderRadius: "4px",
    minHeight: "44px",
    boxShadow: "none",
    "&:hover": { borderColor: "#a4a3b1" },
  }),
  menu: (base) => ({
    ...base,
    background: "#2e2e38",
    border: "1px solid #747480",
    borderRadius: "4px",
    zIndex: 9999,
    position: "absolute",
  }),
  menuList: (base) => ({ ...base, padding: 0, maxHeight: 200 }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? "#3a3a4a" : "transparent",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "14px",
    padding: "10px 12px",
    "&:active": { background: "#3a3a4a" },
  }),
  singleValue: (base) => ({ ...base, color: "#ffffff", fontSize: "14px" }),
  multiValue: (base) => ({
    ...base,
    background: "#3a3a4a",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#ffffff", fontSize: "13px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#a4a3b1",
    "&:hover": { background: "#747480", color: "#ffffff" },
  }),
  input: (base) => ({
    ...base,
    color: "#ffffff",
    fontSize: "14px",
  }),
  placeholder: (base) => ({ ...base, color: "#747480", fontSize: "14px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#a4a3b1",
    "&:hover": { color: "#ffffff" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#a4a3b1",
    "&:hover": { color: "#ffffff" },
  }),
  noOptionsMessage: (base) => ({ ...base, color: "#747480", fontSize: "14px" }),
};

const COE_OPTIONS = [
  { value: "ADN", label: "ADN" },
  { value: "AOA", label: "AOA" },
];

const NEEDS_SUPER_REGION_MIN_ID = 5;
const NEEDS_REGION_MIN_ID = 6;
const NEEDS_COUNTRY_MIN_ID = 7;

export function AddUserModal({
  show,
  onClose,
  onAssign = () => {},
  initialValues = null,
  loading = false,
  error = "",
  onClearError = () => {},
  isViewMode = false,
  currentUserEmail = "",
}) {
  const dispatch = useDispatch();
  const { getADUsers } = useADUsers();
  const { t } = useI18n();

  const {
  roles,
  superRegions,
  regions,
  regionsLoading,
  countries,
  countriesLoading,
  nearshoreCountries,
  nearshoreCountriesLoading,
  rows: allUsers,
} = useSelector((state) => state.userManagement);
  

  const isEditMode = Boolean(initialValues) && !isViewMode;
  const isExistingUser = Boolean(initialValues);
  const initializedRef = useRef(false);
  const pendingRegionIDsRef = useRef([]);
  const pendingCountryIdsRef = useRef([]);
  const pendingNearshoreCountryIdsRef = useRef([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCoe, setSelectedCoe] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedSuperRegions, setSelectedSuperRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedNearshoreCountries, setSelectedNearshoreCountries] = useState([]);
  const selectedRoleIdNum = selectedRole?.value ?? 0;
  const needsSuperRegion = selectedRoleIdNum >= NEEDS_SUPER_REGION_MIN_ID;
  const needsRegion = selectedRoleIdNum >= NEEDS_REGION_MIN_ID;
  const needsCountry = selectedRoleIdNum >= NEEDS_COUNTRY_MIN_ID;

  const HIDDEN_ROLE_NAMES = ["Engagement Manager", "Admin", "Regional Level Reporting User"];


  const roleOptions = useMemo(() => {
  const baseRoles = roles.filter(
    (role) => !HIDDEN_ROLE_NAMES.includes(role.roleName),
  );

  const selectedEmail = selectedUser?.label?.toLowerCase() || "";
  const hasNearshoreEntry =
    selectedEmail.length > 0 &&
    allUsers.some(
      (u) =>
        u.email?.toLowerCase() === selectedEmail &&
        String(u.processingTeamType).toLowerCase().includes("nearshore"),
    );

  const scopedRoles =
    selectedTeam === "GDS" && !hasNearshoreEntry
      ? baseRoles
      : baseRoles.filter(
          (role) => role.roleName === "Resource Manager/PMO",
        );

  return scopedRoles.map((role) => ({
    value: role.roleMasterID,
    label: role.roleName,
  }));
}, [roles, selectedTeam, selectedUser, allUsers]);


  const superRegionOptions = useMemo(
    () => superRegions.map((sr) => ({ value: sr.superRegionID, label: sr.name })),
    [superRegions],
  );

  const regionOptions = useMemo(
    () => regions.map((r) => ({ value: r.regionID, label: r.name })),
    [regions],
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.countryId, label: c.countryName })),
    [countries],
  );

  const nearshoreCountryOptions = useMemo(
  () =>
    nearshoreCountries.map((c) => ({
      value: c.countryId,
      label: c.countryName,
    })),
  [nearshoreCountries],
);

  const resetForm = useCallback(() => {
    setSelectedUser(null);
    setSelectedRole(null);
    setSelectedCoe(null);
    setSelectedTeam(null);
    setSelectedSuperRegions([]);
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSelectedNearshoreCountries([]);
    pendingRegionIDsRef.current = [];
    pendingCountryIdsRef.current = [];
    pendingNearshoreCountryIdsRef.current = [];
    dispatch(clearRegions());
    dispatch(clearCountries());
    initializedRef.current = false;
  }, [dispatch]);

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }

    if (initializedRef.current) return;

    if (initialValues) {
      if (roles.length === 0) return;

      initializedRef.current = true;

      const initialEmail = initialValues.email || "";
      setSelectedUser(
        initialEmail
          ? {
              id: initialValues.userId || initialValues.id || initialEmail,
              label: initialEmail,
              value: initialValues.displayName || initialEmail,
            }
          : null,
      );

      const matchedRole = roles.find((r) => r.roleName === initialValues.role);
      setSelectedRole(
        matchedRole
          ? { value: matchedRole.roleMasterID, label: matchedRole.roleName }
          : null,
      );

      const coeVal = initialValues.coe && initialValues.coe !== "--" ? initialValues.coe : "";
      setSelectedCoe(coeVal ? { value: coeVal, label: coeVal } : null);

      const initialTeam = Array.isArray(initialValues.processingTeamType)
      ? initialValues.processingTeamType[0] || ""
      : initialValues.processingTeamType || "";

  setSelectedTeam(initialTeam);

  const initialNearshoreCountryIds =
    initialValues.processingTeamCountryIds ||
    [];

  if (
    initialTeam === "Nearshore" &&
      initialNearshoreCountryIds.length > 0
  ) {
    pendingNearshoreCountryIdsRef.current =
      initialNearshoreCountryIds.map(Number);
  }

    const initSuperRegionIDs = initialValues.superRegionIDs || [];
    const initRegionIDs = initialValues.regionIDs || [];
    const initCountryIds = initialValues.countryIds || [];

      if (initSuperRegionIDs.length > 0 && superRegions.length > 0) {
        const matched = superRegions
          .filter((sr) => initSuperRegionIDs.includes(sr.superRegionID))
          .map((sr) => ({ value: sr.superRegionID, label: sr.name }));
        setSelectedSuperRegions(matched);
      }

      const roleId = matchedRole?.roleMasterID || 0;

      if (roleId >= NEEDS_REGION_MIN_ID && initSuperRegionIDs.length > 0) {
        pendingRegionIDsRef.current = initRegionIDs;
        dispatch(fetchRegionsThunk({ superRegionIDs: initSuperRegionIDs }));
      }

      if (roleId >= NEEDS_COUNTRY_MIN_ID && initSuperRegionIDs.length > 0 && initRegionIDs.length > 0) {
        pendingCountryIdsRef.current = initCountryIds;
        dispatch(
          fetchCountriesThunk({
            superRegionIDs: initSuperRegionIDs,
            regionIDs: initRegionIDs,
          }),
        );
      }
    } else {
      initializedRef.current = true;
    }
  }, [show, initialValues, roles, superRegions, dispatch, resetForm]);

  useEffect(() => {
  if (pendingRegionIDsRef.current.length > 0 && regions.length > 0) {
    const ids = pendingRegionIDsRef.current;

    const matched = regions
      .filter((r) => ids.includes(r.regionID))
      .map((r) => ({
        value: r.regionID,
        label: r.name,
      }));

    setSelectedRegions(matched);
    pendingRegionIDsRef.current = [];
  }
}, [regions]);

  useEffect(() => {
    if (pendingCountryIdsRef.current.length > 0 && countries.length > 0) {
      const ids = pendingCountryIdsRef.current;
      const matched = countries
        .filter((c) => ids.includes(c.countryId))
        .map((c) => ({ value: c.countryId, label: c.countryName }));
      setSelectedCountries(matched);
      pendingCountryIdsRef.current = [];
    }
  }, [countries]);


  useEffect(() => {
  if (selectedTeam === "Nearshore") {
    dispatch(fetchNearshoreCountriesThunk());
  }

  if (selectedTeam === "GDS" && !isViewMode) {
    setSelectedNearshoreCountries([]);
    pendingNearshoreCountryIdsRef.current = [];
  }
}, [selectedTeam, dispatch, isViewMode]);


  useEffect(() => {
    if (isViewMode) return;
  if (
    selectedTeam !== "GDS" &&
    selectedRole &&
    selectedRole.label !== "Resource Manager/PMO"
  ) {
    setSelectedRole(null);
    setSelectedSuperRegions([]);
    setSelectedRegions([]);
    setSelectedCountries([]);
    dispatch(clearRegions());
    dispatch(clearCountries());
  }
}, [isViewMode, selectedTeam, selectedRole, dispatch]);

  useEffect(() => {
  if (
    pendingNearshoreCountryIdsRef.current.length > 0 &&
    nearshoreCountries.length > 0
  ) {
    const pendingIds =
      pendingNearshoreCountryIdsRef.current.map(Number);

    const matchedNearshoreCountries = nearshoreCountries
      .filter((country) =>
        pendingIds.includes(Number(country.countryId)),
      )
      .map((country) => ({
        value: country.countryId,
        label: country.countryName,
      }));

    setSelectedNearshoreCountries(matchedNearshoreCountries);
    pendingNearshoreCountryIdsRef.current = [];
  }
}, [nearshoreCountries]);

  const handleRoleChange = (option) => {
    setSelectedRole(option);
    setSelectedSuperRegions([]);
    setSelectedRegions([]);
    setSelectedCountries([]);
    dispatch(clearRegions());
    dispatch(clearCountries());
  };

  const handleSuperRegionChange = (options) => {
    const selected = options || [];
    setSelectedSuperRegions(selected);
    setSelectedRegions([]);
    setSelectedCountries([]);
    dispatch(clearCountries());

    const ids = selected.map((o) => o.value);
    if (ids.length > 0 && needsRegion) {
      dispatch(fetchRegionsThunk({ superRegionIDs: ids }));
    } else {
      dispatch(clearRegions());
    }
  };

  const handleRegionChange = (options) => {
    const selected = options || [];
    setSelectedRegions(selected);
    setSelectedCountries([]);

    const ids = selected.map((o) => o.value);
    const srIds = selectedSuperRegions.map((o) => o.value);
    if (ids.length > 0 && srIds.length > 0 && needsCountry) {
      dispatch(fetchCountriesThunk({ superRegionIDs: srIds, regionIDs: ids }));
    } else {
      dispatch(clearCountries());
    }
  };

  const handleCountryChange = (options) => {
    setSelectedCountries(options || []);
  };
  const handleNearshoreCountryChange = (options) => {
  setSelectedNearshoreCountries(options || []);
};

  const isSelfAssignment =
    Boolean(selectedUser?.label) &&
    Boolean(currentUserEmail) &&
    selectedUser.label.toLowerCase() === currentUserEmail.toLowerCase();

  const isAssignDisabled = useMemo(() => {
    if (!selectedUser?.label || !selectedRole || !selectedCoe) return true;
    if (isSelfAssignment) return true;
    if (needsSuperRegion && selectedSuperRegions.length === 0) return true;
    if (needsRegion && selectedRegions.length === 0) return true;
    if (needsCountry && selectedCountries.length === 0) return true;
    if (selectedTeam === "Nearshore" &&  selectedNearshoreCountries.length === 0) return true;
    return false;
  }, [
    selectedUser,
    selectedRole,
    selectedCoe,
    selectedTeam,
    needsSuperRegion,
    selectedSuperRegions,
    needsRegion,
    selectedRegions,
    needsCountry,
    selectedCountries,
    selectedNearshoreCountries,
    isSelfAssignment,
  ]);

  const handleAssignClick = () => {
    if (isViewMode) return;
    if (onClearError) onClearError();
    onAssign({
      ...initialValues,
      email: selectedUser?.label || "",
      firstName: selectedUser?.firstName || "",
      lastName: selectedUser?.lastName || "",
      role: selectedRole?.label || "",
      roleId: selectedRole?.value || 0,
      processingTeamType: selectedTeam,
      coe: selectedCoe?.value || "",
      superRegionIDs: selectedSuperRegions.map((o) => o.value),
      regionIDs: selectedRegions.map((o) => o.value),
      countryIds: selectedCountries.map((o) => o.value),
      processingTeamCountryIds: selectedNearshoreCountries.map((o) => o.value),
    });
  };

  return (
    <MotifModal
      onClose={onClose}
      show={show}
      className="add-user-modal app-modal"
      size="default"
    >
      <MotifModalHeader className="motif-subtitle-default-regular">
        {isViewMode
          ? t("user_mng_viewUser")
          : isEditMode
            ? t("user_mng_editUser")
            : t("user_mng_addUser")}
      </MotifModalHeader>
      <MotifModalBody>
        <div className="add-user-modal-body">
          <div className="add-user-field-group">
            <MotifLabel className="motif-body2-default-light" htmlFor="add-user-email">
              {t("user_mng_userEmail")}
            </MotifLabel>
            <div
              className={isExistingUser ? "add-user-email-locked" : undefined}
              title={
                isExistingUser
                  ? t("user_mng_emailNotEditable", {
                      defaultValue: "User email cannot be modified",
                    })
                  : undefined
              }
            >
              <MultiPeoplePicker
                getADUsers={getADUsers}
                onChange={(opt) => {
                  if (isViewMode) return;
                  setSelectedUser(opt || null);

                  if (!opt?.label) {
                    setSelectedTeam(null);
                    return;
                  }

                  setSelectedTeam(
                    isGdsEmail(opt.label) ? "GDS" : "Nearshore"
                  );
                }}
                options={[]}
                disabledPicker={isExistingUser}
                isMandatory={true}
                label="Users"
                value={selectedUser}
                isErrorVisible={false}
                errorMessage=""
                isErrorVisible2={false}
                errorMessage2=""
                placeholder={t("user_mng_userEmailPlaceholder")}
                pageName="User Management"
              />
              {isSelfAssignment && (
                <p style={{ color: "#ff4d4d", fontSize: "13px", marginTop: "6px" }}>
                  You cannot add yourself as a team member.
                </p>
              )}
            </div>
            <div className="add-user-field-group">
  <MotifLabel className="motif-body2-default-light">
    Team
  </MotifLabel>

  <div className="team-radio-group">
    <label style={{ marginLeft: "20px" }}>
      <input
        type="radio"
        name="team"
        value="Nearshore"
        checked={selectedTeam === "Nearshore"}
        onChange={(event) => {
        if (isViewMode) return;

        if (event.target.value !== selectedTeam) {
          setSelectedRole(null);
        }

        setSelectedTeam(event.target.value);
      }}
      />
      Nearshore Team
    </label>

    <label>
      <input
        type="radio"
        name="team"
        value="GDS"
        checked={selectedTeam === "GDS"}
        onChange={(event) => {
          if (isViewMode) return;

          if (event.target.value !== selectedTeam) {
            setSelectedRole(null);
          }

          setSelectedTeam(event.target.value);
        }}
        disabled={
          !isViewMode &&
          Boolean(
            selectedUser?.label &&
              !isGdsEmail(selectedUser.label),
          )
        }
      />
      GDS Team
    </label>
  </div>
</div>
{selectedTeam === "Nearshore" && (
  <div className="add-user-field-group">
    <MotifLabel className="motif-body2-default-light">
      Nearshore Country
    </MotifLabel>

    <ReactSelect
      isMulti
      value={selectedNearshoreCountries}
      onChange={handleNearshoreCountryChange}
      options={nearshoreCountryOptions}
      placeholder={
        nearshoreCountriesLoading
          ? t("loading")
          : "Select Nearshore Countries"
      }
      isDisabled={isViewMode || nearshoreCountriesLoading}
      isLoading={nearshoreCountriesLoading}
      styles={darkSelectStyles}
      menuPosition="fixed"
      closeMenuOnSelect={false}
    />
  </div>
)}

          </div>

          <div className="add-user-field-group">
            <MotifLabel className="motif-body2-default-light">{t("user_mng_role")}</MotifLabel>
            <ReactSelect
              value={selectedRole}
              onChange={handleRoleChange}
              options={roleOptions}
              placeholder={t("user_mng_selectRole")}
              styles={darkSelectStyles}
              menuPosition="fixed"
              isClearable={!isViewMode}
              isDisabled={isViewMode}
            />
          </div>

          {needsSuperRegion && (
            <div className="add-user-field-group">
              <MotifLabel className="motif-body2-default-light">{t("user_mng_superRegion")}</MotifLabel>
              <ReactSelect
                isMulti
                value={selectedSuperRegions}
                onChange={handleSuperRegionChange}
                options={superRegionOptions}
                placeholder={t("user_mng_selectSuperRegions")}
                styles={darkSelectStyles}
                menuPosition="fixed"
                closeMenuOnSelect={false}
                isDisabled={isViewMode}
              />
            </div>
          )}

          {needsRegion && (
            <div className="add-user-field-group">
              <MotifLabel className="motif-body2-default-light">{t("user_mng_region")}</MotifLabel>
              <ReactSelect
                isMulti
                value={selectedRegions}
                onChange={handleRegionChange}
                options={regionOptions}
                placeholder={regionsLoading ? t("loading") : t("user_mng_selectRegions")}
                isDisabled={isViewMode || selectedSuperRegions.length === 0 || regionsLoading}
                isLoading={regionsLoading}
                styles={darkSelectStyles}
                menuPosition="fixed"
                closeMenuOnSelect={false}
              />
            </div>
          )}

          { needsCountry && (
            <div className="add-user-field-group">
              <MotifLabel className="motif-body2-default-light">{t("user_mng_country")}</MotifLabel>
              <ReactSelect
                isMulti
                value={selectedCountries}
                onChange={handleCountryChange}
                options={countryOptions}
                placeholder={countriesLoading ? t("loading") : t("user_mng_selectCountries")}
                isDisabled={isViewMode || selectedRegions.length === 0 || countriesLoading}
                isLoading={countriesLoading}
                styles={darkSelectStyles}
                menuPosition="fixed"
                closeMenuOnSelect={false}
              />
            </div>
          )}

          
          <div className="add-user-field-group">
            <MotifLabel className="motif-body2-default-light">COE</MotifLabel>
            <ReactSelect
              value={selectedCoe}
              onChange={(opt) => setSelectedCoe(opt)}
              options={COE_OPTIONS}
              placeholder={t("user_mng_selectCoe")}
              styles={darkSelectStyles}
              menuPosition="fixed"
              isClearable={!isViewMode}
              isDisabled={isViewMode}
            />
          </div>
        </div>
      </MotifModalBody>
      {!isViewMode && (
  <MotifModalFooter>
    {error && (
      <div className="add-user-modal-error">
        {error}
      </div>
    )}

    <div className="add-user-modal-footer">
      <Button
        size="small"
        variant="secondary"
        onClick={onClose}
        disabled={loading}
      >
        {t("cancel")}
      </Button>

      <Button
        variant="primary"
        size="small"
        onClick={handleAssignClick}
        disabled={isAssignDisabled || loading}
      >
        {loading
          ? t("user_mng_saving")
          : isEditMode
            ? t("user_mng_update")
            : t("user_mng_assign")}
      </Button>
    </div>
  </MotifModalFooter>
)}
    </MotifModal>
  );
}
